<template>
  <div :class="$style.page">
    <BaseContainer :class="$style.container">
      <form @submit.prevent="submit">
        <BaseLoader
          :class="$style.box"
          :is-active="isFormLoading"
        >
          <FormBox
            :heading="formStepData.heading"
            :lead="formStepData.lead"
          >
            <template v-if="formStep === 1">
              <BaseField
                is-required
                :error="givenNameError"
                :class="$style.field"
              >
                <template #label>
                  GIVEN NAME
                </template>

                <BaseInput
                  v-model="givenName"
                  :has-error="Boolean(givenNameError)"
                />
              </BaseField>
              <BaseField
                is-required
                :error="familyNameError"
                :class="$style.field"
              >
                <template #label>
                  FAMILY NAME
                </template>

                <BaseInput
                  v-model="familyName"
                  :has-error="Boolean(familyNameError)"
                />
              </BaseField>
              <BaseField
                is-required
                :class="$style.field"
                :error="timeZoneError"
              >
                <template #label>
                  TIME ZONE
                </template>

                <BaseDropdown
                  placement="bottom-start"
                >
                  <template #trigger>
                    <div :class="$style.selectedTimeZone">
                      {{ timeZone }}
                    </div>
                  </template>

                  <template #content>
                    <div :class="$style.timeZonesDropdown">
                      <BaseDropdownHeading>
                        Select a time zone
                      </BaseDropdownHeading>

                      <div :class="$style.searchContainer">
                        <input
                          ref="searchInput"
                          v-model="timeZonesFilter"
                          type="text"
                          :class="$style.searchInput"
                        >
                        <MagnifierSvg
                          :class="$style.magnifier"
                        />
                      </div>

                      <div :class="$style.timeZonesList">
                        <BaseDropdownActionsList>
                          <BaseDropdownActionRadio
                            v-for="option in filteredTimeZonesList"
                            :key="option"
                            v-model="timeZone"
                            :value="option"
                          >
                            {{ option }}
                          </BaseDropdownActionRadio>
                        </BaseDropdownActionsList>
                      </div>
                    </div>
                  </template>
                </BaseDropdown>
              </BaseField>
            </template>

            <template #footer>
              <BaseButton
                variant="success"
                type="submit"
                is-block
              >
                {{ formStepData.submitText }}
              </BaseButton>
            </template>
          </FormBox>
        </BaseLoader>
      </form>
    </BaseContainer>
  </div>
</template>

<script>
import gql from 'graphql-tag';
import { mapActions, mapState } from 'vuex';
import BaseContainer from '@/components/base/Container';
import BaseField from '@/components/base/Field';
import BaseInput from '@/components/base/Input';
import BaseButton from '@/components/base/Button';
import BaseLoader from '@/components/base/Loader';
import BaseDropdown, {
  DropdownActionRadio as BaseDropdownActionRadio,
  DropdownHeading as BaseDropdownHeading,
  DropdownActionsList as BaseDropdownActionsList,
} from '@/components/base/Dropdown';
import FormBox from '@/components/FormBox';
import awsApi from '@/aws-api';
import { timeZones } from '@/config';
import MagnifierSvg from '@/assets/images/icons/magnifier.svg?inline';

export default {
  components: {
    BaseContainer,
    BaseField,
    BaseInput,
    BaseButton,
    BaseLoader,
    FormBox,
    BaseDropdown,
    BaseDropdownActionRadio,
    BaseDropdownHeading,
    BaseDropdownActionsList,
    MagnifierSvg,
  },
  TIME_ZONES_OPTIONS: timeZones,
  FORM_STEPS_DATA: [
    {
      heading: 'Edit profile',
      submitText: 'Save',
    },
    {
      heading: 'Profile saved',
      submitText: 'Go to the main page',
    },
  ],
  data() {
    return {
      givenName: '',
      givenNameError: null,
      familyName: '',
      familyNameError: null,
      timeZone: null,
      timeZoneError: null,
      isFormLoading: false,
      formStep: 1,
      timeZonesFilter: null,
    };
  },
  computed: {
    ...mapState('user', ['user']),
    formStepData() {
      return this.$options.FORM_STEPS_DATA[this.formStep - 1];
    },
    filteredTimeZonesList() {
      if (!this.timeZonesFilter) {
        return this.$options.TIME_ZONES_OPTIONS;
      }
      return this.$options.TIME_ZONES_OPTIONS.filter(
        (value) => value.toLowerCase().includes(this.timeZonesFilter.toLowerCase()),
      );
    },
  },
  created() {
    this.givenName = this.user.givenName;
    this.familyName = this.user.familyName;
    this.timeZone = this.user.timeZone;
  },
  methods: {
    ...mapActions('user', { reloadUser: 'reload' }),
    async saveUser() {
      this.givenNameError = null;
      this.familyNameError = null;
      this.timeZoneError = null;
      let hasError = false;

      if (this.givenName.trim() === '') {
        hasError = true;
        this.givenNameError = 'Can\'t be empty';
      }

      if (this.familyName.trim() === '') {
        hasError = true;
        this.familyNameError = 'Can\'t be empty';
      }

      if (this.timeZone == null) {
        hasError = true;
        this.timeZoneError = 'Can\'t be empty';
      }

      if (hasError) {
        return;
      }

      this.isFormLoading = true;

      try {
        await Promise.all([
          awsApi.updateUserAttributes({
            given_name: this.givenName,
            family_name: this.familyName,
          }),
          this.$apollo.mutate({
            mutation: gql`mutation ($userId: String!, $timeZone: String!) {
              update_users_by_pk(pk_columns: {id: $userId}, _set: {time_zone: $timeZone}) {
                id
              }
            }`,
            variables: {
              userId: this.user.username,
              timeZone: this.timeZone,
            },
          }),
        ]);

        await this.reloadUser();

        this.formStep = 2;
        this.isFormLoading = false;
      } catch (e) {
        console.log(e); // eslint-disable-line no-console
      }
    },
    submit() {
      switch (this.formStep) {
        case 1:
          return this.saveUser();
        case 2:
          return this.$router.push({ name: 'Home' });
        default:
          return null;
      }
    },
  },
};
</script>

<style lang="scss" module>
.page {
  flex-grow: 1;
  padding-top: map-get($page-vertical-paddings, 'small');
  padding-bottom: map-get($page-vertical-paddings, 'small');
  background: #fafafa;
}

.container {
  display: flex;
  justify-content: center;
}

.box {
  width: 470px;
}

.field {

  &:not(:first-child) {
    margin-top: 20px;
  }
}

[data-v-tippy] {
  display: block;
}

.selectedTimeZone {
  padding: 10px 8px;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  color: $title;
  cursor: pointer;
  border: 2px solid $border;
  border-radius: 5px;
}

.timeZonesDropdown {
  width: 410px;
}

.searchContainer {
  position: relative;
  display: flex;
  width: 100%;
  padding: 0 8px 8px;
}

.searchInput {
  @include reset;
  position: relative;
  width: 100%;
  padding: 5px 25px 5px 7px;
  font-size: 15px;
  color: $title;
  background-color: $background-light;
  border-radius: 5px;
}

.magnifier {
  position: absolute;
  top: 50%;
  right: 18px;
  width: 10px;
  height: auto;
  color: $disabled;
  pointer-events: none;
  transform: translateY(calc(-50% - 4px));
}

.timeZonesList {
  max-height: 200px;
  overflow: auto;
}
</style>
